import { supabase } from '../lib/supabase';

/**
 * One row of operations.issue_categories — the operator-facing taxonomy for
 * manual "Mark Issue" markers. Adding a category is one DB row, no release.
 */
export interface IssueCategory {
  key: string;
  label: string;
  sort: number;
  active: boolean;
}

/**
 * Hardcoded fallback matching the migration seed, so Mark Issue keeps working
 * before the issue_categories migration is applied (or if the query fails).
 */
const DEFAULT_CATEGORIES: IssueCategory[] = [
  { key: 'customer_disconnect', label: 'Customer disconnect', sort: 10, active: true },
  { key: 'screendooring', label: 'Screendooring', sort: 20, active: true },
  { key: 'lag_stutter', label: 'Lag / stutter', sort: 30, active: true },
  { key: 'audio', label: 'Audio', sort: 40, active: true },
  { key: 'no_ai_conversation', label: 'No AI conversation', sort: 50, active: true },
  { key: 'pico_wont_connect', label: "PICO won't connect", sort: 60, active: true },
  { key: 'experience_no_vr', label: 'Experience running but no VR', sort: 70, active: true },
  { key: 'other', label: 'Other', sort: 90, active: true },
];

/**
 * Fetch-once cache (same pattern as rolePermissions). Only successful,
 * non-empty results are cached so a pre-migration deploy retries next time.
 */
let categoriesCache: IssueCategory[] | null = null;

export async function getIssueCategories(): Promise<IssueCategory[]> {
  if (categoriesCache) {
    return categoriesCache;
  }

  try {
    const { data, error } = await supabase
      .schema('operations')
      .from('issue_categories')
      .select('key, label, sort, active')
      .eq('active', true)
      .order('sort', { ascending: true });

    if (error || !data || data.length === 0) {
      if (error) {
        console.error('Error fetching issue categories (using defaults):', error);
      }
      return DEFAULT_CATEGORIES;
    }

    categoriesCache = data as IssueCategory[];
    return categoriesCache;
  } catch (err) {
    console.error('Exception fetching issue categories (using defaults):', err);
    return DEFAULT_CATEGORIES;
  }
}

/** Clear the cache (e.g. after an admin edits categories). */
export function clearIssueCategoriesCache(): void {
  categoriesCache = null;
}
